import React, { useState, useEffect, useRef, useCallback } from 'react';
import { sendCode, verifyCode } from '../api/sms';
import axios from 'axios';

interface SmsVerifyStepProps {
  phone: string;
  purpose: 'signup' | 'password_reset' | 'phone_change';
  onVerified: () => void;
  disabled?: boolean;
}

const SmsVerifyStep: React.FC<SmsVerifyStepProps> = ({
  phone,
  purpose,
  onVerified,
  disabled = false,
}) => {
  const [step, setStep] = useState<'idle' | 'code_sent' | 'verified'>('idle');
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startCooldown = useCallback((seconds: number) => {
    setCooldown(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleSend = async () => {
    if (!phone || phone.length < 10) {
      setError('휴대폰 번호를 확인해주세요');
      return;
    }
    setLoading(true);
    setError(null);
    setInfo(null);
    try {
      const res = await sendCode(phone, purpose);
      setStep('code_sent');
      startCooldown(res.cooldown_seconds);
      setInfo(
        res.test_mode
          ? '테스트 모드: 인증번호 000000 을 입력하세요'
          : '인증번호가 발송되었습니다',
      );
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.error || 'SMS 발송에 실패했습니다');
      } else {
        setError('SMS 발송에 실패했습니다');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (code.length < 4) {
      setError('인증번호를 입력해주세요');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await verifyCode(phone, purpose, code);
      setStep('verified');
      setInfo('인증이 완료되었습니다');
      onVerified();
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.error || '인증에 실패했습니다');
      } else {
        setError('인증에 실패했습니다');
      }
    } finally {
      setLoading(false);
    }
  };

  const isVerified = step === 'verified';

  return (
    <div className="sms-verify-step">
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <button
          type="button"
          onClick={handleSend}
          disabled={disabled || loading || cooldown > 0 || isVerified}
          style={{ whiteSpace: 'nowrap' }}
        >
          {cooldown > 0
            ? `재발송 (${cooldown}s)`
            : step === 'idle'
              ? '인증번호 발송'
              : '재발송'}
        </button>
      </div>

      {step !== 'idle' && !isVerified && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="인증번호 6자리"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            disabled={loading || isVerified}
            style={{ width: 140 }}
          />
          <button
            type="button"
            onClick={handleVerify}
            disabled={loading || code.length < 4 || isVerified}
          >
            확인
          </button>
        </div>
      )}

      {isVerified && (
        <p style={{ color: '#4CAF50', marginTop: 8, fontSize: 14 }}>
          ✓ 인증 완료
        </p>
      )}

      {error && (
        <p style={{ color: '#F44336', marginTop: 4, fontSize: 13 }}>{error}</p>
      )}
      {info && !error && (
        <p style={{ color: '#666', marginTop: 4, fontSize: 13 }}>{info}</p>
      )}
    </div>
  );
};

export default SmsVerifyStep;
